import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { getBondChatSocket } from "./bondchat/generalChatSingleton";
import { initializeSocket } from "./bondchat/generalChatSlice";

const API_BASE = `${API_BASE_URL}/api`;
const DRAFT_IDLE_MS = 2500;
const DRAFT_THROTTLE_MS = 120;
const DRAFT_EXPIRY_MS = 5000;

const toIdStr = (id) => {
  if (id == null) return "";
  if (typeof id === "object") return id._id?.toString?.() ?? id.toString?.();
  return String(id);
};

const extractError = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "Something went wrong";

const draftExpiryTimers = new Map();
let draftListenersAttached = false;
let activeBetChatId = null;
let lastDraftEmitAt = 0;
let draftIdleTimer = null;
let lastEmittedDraft = "";

const clearDraftExpiry = (userId) => {
  const key = String(userId);
  if (draftExpiryTimers.has(key)) {
    clearTimeout(draftExpiryTimers.get(key));
    draftExpiryTimers.delete(key);
  }
};

export const fetchChats = createAsyncThunk(
  "betChat/fetchChats",
  async (betId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_BASE}/${toIdStr(betId)}/chats`);
      return data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  }
);

export const sendChat = createAsyncThunk(
  "betChat/sendChat",
  async ({ betId, userId, message }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_BASE}/${toIdStr(betId)}/chats`, {
        userId: toIdStr(userId),
        message,
      });
      return data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  }
);

export const sendLike = createAsyncThunk(
  "betChat/sendLike",
  async ({ messageId, userId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${API_BASE}/chats/${toIdStr(messageId)}/like`,
        { userId: toIdStr(userId) }
      );
      return { messageId, likes: data.likes };
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  }
);

const betChatSlice = createSlice({
  name: "betChat",
  initialState: {
    chats: [],
    loading: false,
    sending: false,
    error: null,
    liveDrafts: {},
    socketReady: false,
  },
  reducers: {
    clearChatError: (state) => {
      state.error = null;
    },
    clearChats: (state) => {
      state.chats = [];
      state.loading = false;
      state.sending = false;
      state.error = null;
      state.liveDrafts = {};
      state.socketReady = false;
    },
    upsertChatMessage: (state, action) => {
      const msg = action.payload;
      const id = toIdStr(msg?._id);
      if (!id) return;
      if (!state.chats.some((c) => toIdStr(c._id) === id)) {
        state.chats.push(msg);
      }
    },
    updateChatLikes: (state, action) => {
      const { messageId, likes } = action.payload || {};
      const mid = toIdStr(messageId);
      const idx = state.chats.findIndex((c) => toIdStr(c._id) === mid);
      if (idx !== -1) state.chats[idx].likes = likes;
    },
    setLiveDraft: (state, action) => {
      const { userId, name, text, isTyping } = action.payload || {};
      if (!userId) return;
      if (!state.liveDrafts) state.liveDrafts = {};
      const key = String(userId);
      if (isTyping && text) {
        state.liveDrafts[key] = {
          userId: key,
          name: name || "Someone",
          text: String(text).slice(0, 280),
        };
      } else {
        delete state.liveDrafts[key];
      }
    },
    clearLiveDrafts: (state) => {
      state.liveDrafts = {};
    },
    setBetChatSocketReady: (state, action) => {
      state.socketReady = Boolean(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChats.pending, (state) => {
        if (state.chats.length === 0) state.loading = true;
        state.error = null;
      })
      .addCase(fetchChats.fulfilled, (state, action) => {
        state.loading = false;
        state.chats = action.payload;
      })
      .addCase(fetchChats.rejected, (state, action) => {
        state.loading = false;
        if (state.chats.length === 0) state.error = action.payload;
      })
      .addCase(sendChat.pending, (state) => {
        state.sending = true;
        state.error = null;
      })
      .addCase(sendChat.fulfilled, (state, action) => {
        state.sending = false;
        const id = toIdStr(action.payload?._id);
        if (id && !state.chats.some((c) => toIdStr(c._id) === id)) {
          state.chats.push(action.payload);
        }
      })
      .addCase(sendChat.rejected, (state, action) => {
        state.sending = false;
        state.error = action.payload;
      })
      .addCase(sendLike.fulfilled, (state, action) => {
        const { messageId, likes } = action.payload;
        const mid = toIdStr(messageId);
        const idx = state.chats.findIndex((c) => toIdStr(c._id) === mid);
        if (idx !== -1) state.chats[idx].likes = likes;
      })
      .addCase(sendLike.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const {
  clearChatError,
  clearChats,
  upsertChatMessage,
  updateChatLikes,
  setLiveDraft,
  clearLiveDrafts,
  setBetChatSocketReady,
} = betChatSlice.actions;

export const selectBetChats = (state) => state.betChat.chats;
export const selectBetChatLoading = (state) => state.betChat.loading;
export const selectBetChatSending = (state) => state.betChat.sending;
export const selectBetChatError = (state) => state.betChat.error;
export const selectBetChatLiveDrafts = (state) => state.betChat.liveDrafts || {};
export const selectBetChatSocketReady = (state) => state.betChat.socketReady;

const attachBetChatListeners = (dispatch, getState, userId) => {
  const socket = getBondChatSocket();
  if (!socket || socket.__betChatListenersAttached) return;
  socket.__betChatListenersAttached = true;
  draftListenersAttached = true;

  socket.on("betChat:newMessage", (message) => {
    if (!message) return;
    const msgBetId = toIdStr(message.betId);
    if (activeBetChatId && msgBetId && msgBetId !== activeBetChatId) return;
    dispatch(upsertChatMessage(message));
    const authorId = toIdStr(message.userId?._id ?? message.userId);
    if (authorId) {
      clearDraftExpiry(authorId);
      dispatch(setLiveDraft({ userId: authorId, isTyping: false, text: "" }));
    }
  });

  socket.on("betChat:likesUpdate", ({ messageId, likes }) => {
    dispatch(updateChatLikes({ messageId, likes }));
  });

  socket.on("betChat:draftUpdate", (payload) => {
    const { userId: draftUserId, name, text, isTyping, betId } = payload || {};
    if (!draftUserId || String(draftUserId) === String(userId)) return;
    if (activeBetChatId && betId && String(betId) !== activeBetChatId) return;

    const key = String(draftUserId);
    clearDraftExpiry(key);
    dispatch(
      setLiveDraft({
        userId: key,
        name,
        text,
        isTyping: Boolean(isTyping) && Boolean(text),
      })
    );

    if (isTyping && text) {
      draftExpiryTimers.set(
        key,
        setTimeout(() => {
          draftExpiryTimers.delete(key);
          dispatch(setLiveDraft({ userId: key, isTyping: false, text: "" }));
        }, DRAFT_EXPIRY_MS)
      );
    }
  });
};

export const joinBetChatRoom =
  ({ betId, userId, name }) =>
  (dispatch) => {
    if (!betId || !userId) return;
    dispatch(initializeSocket(userId));
    const socket = getBondChatSocket();
    if (!socket) return;

    activeBetChatId = toIdStr(betId);
    attachBetChatListeners(dispatch, null, userId);
    dispatch(clearLiveDrafts());

    const emitJoin = () => {
      socket.emit(
        "joinBetChat",
        { betId: activeBetChatId, userId, name },
        (res) => {
          dispatch(setBetChatSocketReady(res?.status === "success"));
        }
      );
    };

    if (socket.connected) emitJoin();
    else socket.once("connect", emitJoin);

    dispatch(setBetChatSocketReady(Boolean(socket.connected)));
  };

export const leaveBetChatRoom =
  ({ betId }) =>
  (dispatch) => {
    const socket = getBondChatSocket();
    const id = toIdStr(betId);
    if (socket && id) {
      socket.emit("leaveBetChat", { betId: id });
      if (lastEmittedDraft) {
        socket.emit("betChat:draft", {
          betId: id,
          text: "",
          isTyping: false,
        });
      }
    }
    if (draftIdleTimer) {
      clearTimeout(draftIdleTimer);
      draftIdleTimer = null;
    }
    lastEmittedDraft = "";
    lastDraftEmitAt = 0;
    activeBetChatId = null;
    draftExpiryTimers.forEach((t) => clearTimeout(t));
    draftExpiryTimers.clear();
    dispatch(clearLiveDrafts());
    dispatch(setBetChatSocketReady(false));
  };

export const emitBetChatDraft =
  ({ betId, userId, name, text }) =>
  () => {
    const socket = getBondChatSocket();
    const id = toIdStr(betId);
    if (!socket || !id || !userId) return;

    const trimmed = String(text || "").slice(0, 280);
    const isTyping = trimmed.trim().length > 0;

    const send = (force = false) => {
      const now = Date.now();
      if (!force && now - lastDraftEmitAt < DRAFT_THROTTLE_MS) return;
      if (!force && trimmed === lastEmittedDraft) return;
      lastDraftEmitAt = now;
      lastEmittedDraft = trimmed;
      socket.emit("betChat:draft", {
        betId: id,
        userId,
        name,
        text: isTyping ? trimmed : "",
        isTyping,
      });
    };

    send(false);

    if (draftIdleTimer) clearTimeout(draftIdleTimer);
    if (isTyping) {
      draftIdleTimer = setTimeout(() => {
        lastEmittedDraft = "";
        socket.emit("betChat:draft", {
          betId: id,
          userId,
          name,
          text: "",
          isTyping: false,
        });
      }, DRAFT_IDLE_MS);
    } else {
      send(true);
    }
  };

export default betChatSlice.reducer;
